import React, { useState, useEffect, useRef } from 'react';
import { router } from '@inertiajs/react';
import ReactMarkdown from 'react-markdown';

function Chat({ thread_id, messages, error }) {
  const [message, setMessage] = useState('');
  const [conversationHistory, setConversationHistory] = useState(
    Array.isArray(messages) ? messages : (messages ? Object.values(messages) : [])
  );
  const [threadId, setThreadId] = useState(thread_id || null);
  const [showToast, setShowToast] = useState(false); // Toast visibility state
  const [copiedOption, setCopiedOption] = useState(''); // Store the copied option
  const [isSending, setIsSending] = useState(false); // State to manage send button
  
  // Ref to the last message for auto-scrolling
  const lastMessageRef = useRef(null);

  // Scroll to the last message whenever conversationHistory updates
  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [conversationHistory]);

  useEffect(() => {
    if (error) {
      console.error("Error:", error);
    }
  }, [error]);

  const handleMessageSubmit = (e) => {
    e.preventDefault();
    setIsSending(true); // Disable the button and set it to "Sending..."

    if (!threadId) {
      router.post('/threads/create', { message }, {
        onSuccess: (response) => {
          setThreadId(response.props.thread_id);
          setConversationHistory(Array.isArray(response.props.messages) ? response.props.messages : (response.props.messages ? Object.values(response.props.messages) : []));
          setMessage('');
          setIsSending(false); // Re-enable the button after the message is sent
        },
        onError: (error) => {
          console.error("Error creating thread:", error);
          setIsSending(false); // Re-enable the button in case of error
        }
      });
    } else {
      router.post(`/threads/${threadId}/ask`, { message }, {
        onSuccess: (response) => {
          setConversationHistory(Array.isArray(response.props.messages) ? response.props.messages : (response.props.messages ? Object.values(response.props.messages) : []));
          setMessage('');
          setIsSending(false); // Re-enable the button after the message is sent
        },
        onError: (error) => {
          console.error("Error continuing thread:", error);
          setIsSending(false); // Re-enable the button in case of error
        }
      });
    }
  };

  const handleOptionClick = (option) => {
    setMessage(option); // Add to the chat box when "Select" is clicked
  };

  const handleCopy = (option) => {
    navigator.clipboard.writeText(option) // Copy option to clipboard
      .then(() => {
        setCopiedOption(option); // Set the copied option
        setShowToast(true); // Show toast notification
        setTimeout(() => setShowToast(false), 3000); // Hide toast after 3 seconds
      })
      .catch((err) => console.error('Failed to copy text: ', err));
  };

  const handleNewChat = () => {
    window.location.href = '/chat'; // Reload the chat page for new chat
  };

  return (
    <div className="container">
      <div className="row justify-content-center mt-4">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header">Help me write Chat</div>
            <div className="card-body">
              {/* Display the conversation history with oldest at the top */}
              <div className="conversation-history">
                {conversationHistory && conversationHistory
                  .slice() // Copy the array
                  .reverse() // Reverse the order
                  .map((msgObj, index) => (
                    <div 
                      key={index} 
                      className="mb-3 p-3 border rounded bg-light"
                      ref={index === conversationHistory.length - 1 ? lastMessageRef : null} // Add ref to the last message
                    >
                      {/* Display the assistant's message */}
                      <ReactMarkdown>{msgObj.message}</ReactMarkdown>

                      {/* Display options (if any) as text lines */}
                      {msgObj.options && (
                        <div className="options mt-2">
                          {msgObj.options.map((option, i) => (
                            <div key={i} className="d-flex align-items-center mb-2">
                              <span className="me-3">{option}</span>
                              <button
                                className="btn btn-sm btn-outline-primary me-2"
                                onClick={() => handleCopy(option)}
                                disabled={copiedOption === option} // Disable the button if this option was copied
                              >
                                {copiedOption === option ? 'Copied' : 'Copy to Clipboard'}
                              </button>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => handleOptionClick(option)}
                              >
                                Select
                              </button>
                            </div>
                          ))}
                          {/* None of the above button */}
                          <button
                            className="btn btn-outline-danger mt-2"
                            onClick={() => handleOptionClick('None of the above')}
                          >
                            None of the above
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
              </div>

              {/* Error handling */}
              {error && <div className="alert alert-danger">{error}</div>}
              
              {/* User input form */}
              <form onSubmit={handleMessageSubmit}>
                <div className="input-group">
                  <textarea
                    className="form-control"
                    rows="5" // Make the chat box 5 lines high
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="What are you trying to say?"
                    required
                  />
                  <button className="btn btn-primary" type="submit" disabled={isSending}>
                    {isSending ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </form>

              {/* New Chat button */}
              <div className="mt-3">
                <button className="btn btn-warning" onClick={handleNewChat}>
                  New Chat
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div className="toast align-items-center text-white bg-success border-0 position-fixed bottom-0 end-0 p-3" style={{ zIndex: 11 }}>
          <div className="d-flex">
            <div className="toast-body">
              Text copied to clipboard!
            </div>
            <button type="button" className="btn-close btn-close-white me-2 m-auto" aria-label="Close" onClick={() => setShowToast(false)}></button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Chat;
